import { Button, Callout, Checkbox, Field, Input, Select } from '@wtfalch/design';
import { useEffect, useMemo, useState } from 'react';

import type { HoursClient } from './client.ts';
import { WEEKDAYS, type Weekday, type WorkingDay } from './hours.ts';

const DAY_NAME: Record<Weekday, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

type Row = { on: boolean; start: string; end: string };
const DEFAULT_SHIFT = { start: '09:00', end: '17:00' };

/** Whether `Intl.DateTimeFormat` accepts the name (legacy aliases such as
 *  `Asia/Calcutta` included, though `supportedValuesOf` omits them). */
export function isValidTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** The browser's own zone when it is valid, otherwise `UTC`. */
export function defaultTimeZone(): string {
  const own = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return own && isValidTimeZone(own) ? own : 'UTC';
}

function timeZones(): string[] {
  const names =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  return names.includes('UTC') ? names : ['UTC', ...names];
}

/** The saved days as one editable row per weekday; a day with no entry is off.
 *  Zones are handled by the caller: the form shows the first saved day's zone
 *  and saves every day in it. */
function toRows(days: readonly WorkingDay[]): Record<Weekday, Row> {
  const rows = {} as Record<Weekday, Row>;
  for (const weekday of WEEKDAYS) {
    const day = days.find((d) => d.weekday === weekday);
    rows[weekday] = day
      ? { on: true, start: day.start, end: day.end }
      : { on: false, ...DEFAULT_SHIFT };
  }
  return rows;
}

const WEEKDAYS_ON_BY_DEFAULT: readonly Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri'];

/**
 * A person sets their own week: which days they work, from when to when, and
 * the time zone those times are in. Nothing is on file for a new person, so
 * the form starts at Monday to Friday 09:00-17:00 in the browser's zone,
 * unsaved until they press Save.
 */
export function WorkingHoursForm({ client }: { client: HoursClient }) {
  const baseZones = useMemo(timeZones, []);
  const [rows, setRows] = useState<Record<Weekday, Row> | null>(null);
  const [timeZone, setTimeZone] = useState(defaultTimeZone);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null);

  useEffect(() => {
    let live = true;
    client
      .read('me')
      .then((days) => {
        if (!live) return;
        if (days.length === 0) {
          const fresh = toRows([]);
          for (const d of WEEKDAYS_ON_BY_DEFAULT) fresh[d].on = true;
          setRows(fresh);
          return;
        }
        setRows(toRows(days));
        const saved = days[0]?.timeZone;
        if (saved && isValidTimeZone(saved)) setTimeZone(saved);
      })
      .catch(() => live && setLoadError("Couldn't load your working hours."));
    return () => {
      live = false;
    };
  }, [client]);

  const zones = baseZones.includes(timeZone) ? baseZones : [timeZone, ...baseZones];

  if (loadError) return <Callout tone="bad">{loadError}</Callout>;
  if (!rows) return <p className="text-sm text-muted">Loading working hours...</p>;

  const change = (weekday: Weekday, patch: Partial<Row>) => {
    setMessage(null);
    setRows({ ...rows, [weekday]: { ...rows[weekday], ...patch } });
  };

  const save = async () => {
    const days: WorkingDay[] = WEEKDAYS.filter((d) => rows[d].on).map((weekday) => ({
      weekday,
      start: rows[weekday].start,
      end: rows[weekday].end,
      timeZone,
    }));
    for (const day of days) {
      if (!day.start || !day.end || day.end <= day.start) {
        setMessage({
          tone: 'bad',
          text: `${DAY_NAME[day.weekday]}: set a start and an end time, with the end after the start.`,
        });
        return;
      }
    }
    setSaving(true);
    setMessage(null);
    try {
      await client.set('me', days);
      setMessage({ tone: 'good', text: 'Saved. Your colleagues can see these hours.' });
    } catch (error) {
      setMessage({
        tone: 'bad',
        text: error instanceof Error ? error.message : "Couldn't save your working hours.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="hours-form"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <Field label="Time zone" layout="row">
        <Select
          searchable
          value={timeZone}
          onChange={(e) => {
            setMessage(null);
            setTimeZone(e.target.value);
          }}
        >
          {zones.map((zone) => (
            <option key={zone} value={zone}>
              {zone}
            </option>
          ))}
        </Select>
      </Field>
      {WEEKDAYS.map((weekday) => (
        <div className="hours-day" key={weekday}>
          <Checkbox
            label={DAY_NAME[weekday]}
            checked={rows[weekday].on}
            onChange={(on) => change(weekday, { on })}
          />
          {rows[weekday].on && (
            <div className="hours-times">
              <Field label={`${DAY_NAME[weekday]} start`} labelHidden>
                <Input
                  type="time"
                  size="sm"
                  value={rows[weekday].start}
                  onChange={(e) => change(weekday, { start: e.target.value })}
                />
              </Field>
              <span aria-hidden="true">to</span>
              <Field label={`${DAY_NAME[weekday]} end`} labelHidden>
                <Input
                  type="time"
                  size="sm"
                  value={rows[weekday].end}
                  onChange={(e) => change(weekday, { end: e.target.value })}
                />
              </Field>
            </div>
          )}
        </div>
      ))}
      {message && <Callout tone={message.tone}>{message.text}</Callout>}
      <div>
        <Button type="submit" kind="primary" busy={saving}>
          Save working hours
        </Button>
      </div>
    </form>
  );
}
